import React, { useState, useEffect } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  AppBar,
  Toolbar,
  Slide,
  useScrollTrigger,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Box,
  Avatar,
  useMediaQuery,
  useTheme
} from '@mui/material';
import {
  Menu as MenuIcon,
  Language as LanguageIcon,
  LightMode as LightModeIcon,
  DarkMode as DarkModeIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useThemeMode } from '../contexts/ThemeContext';
import { COLORS } from '../theme/colors';
import { getUploadUrl } from '../utils/apiConfig';
import Sidebar, { drawerWidth } from './Sidebar';

/**
 * Navbar Component
 * Top navigation bar with integrated sidebar for responsive layout.
 */
function Navbar() {
  const { t, i18n } = useTranslation();
  const { user, logout, updateLanguage } = useAuth();
  const { themeMode, effectiveTheme, toggleTheme } = useThemeMode();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));

  // State
  const [mobileOpen, setMobileOpen] = useState(false);
  const [languageMenuAnchor, setLanguageMenuAnchor] = useState(null);

  // Hide navbar on scroll down, show on scroll up (mobile only)
  const trigger = useScrollTrigger();
  const [prevScrollPos, setPrevScrollPos] = useState(0);
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    if (!isMobile) {
      setVisible(true);
      return;
    }

    const handleScroll = () => {
      const currentScrollPos = window.scrollY;

      // Show navbar when scrolling up or at top
      const shouldShow = prevScrollPos > currentScrollPos || currentScrollPos < 10;

      setVisible(shouldShow);
      setPrevScrollPos(currentScrollPos);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [prevScrollPos, isMobile]);
  const [profileMenuAnchor, setProfileMenuAnchor] = useState(null);

  // Language menu handlers
  const handleLanguageMenuOpen = (event) => {
    setLanguageMenuAnchor(event.currentTarget);
  };

  const handleLanguageMenuClose = () => {
    setLanguageMenuAnchor(null);
  };

  const handleLanguageChange = async (lang) => {
    i18n.changeLanguage(lang);
    localStorage.setItem('i18nextLng', lang);

    if (user) {
      try {
        await updateLanguage(lang);
      } catch (error) {
        console.error('Failed to update user language:', error);
      }
    }

    handleLanguageMenuClose();
  };

  // Profile menu handlers
  const handleProfileMenuOpen = (event) => {
    setProfileMenuAnchor(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setProfileMenuAnchor(null);
  };

  // Drawer toggle
  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  // Logout handler
  const handleLogout = () => {
    logout();
    navigate('/login');
    handleProfileMenuClose();
  };

  // Navigate handler
  const handleNavigate = (path) => {
    navigate(path);
    handleProfileMenuClose();
  };

  return (
    <Box sx={{ display: 'flex' }}>
      <Slide appear={false} direction="down" in={visible}>
        <AppBar
          position="fixed"
          sx={{
            width: '100%',
            top: 0,
            left: 0,
            zIndex: (theme) => theme.zIndex.drawer + 1,
            boxShadow: effectiveTheme === 'dark' ? '0px 2px 4px -1px rgba(0, 0, 0, 0.3)' : '0px 3px 1px -2px rgba(0, 0, 0, 0.1), 0px 2px 2px 0px rgba(0, 0, 0, 0.1), 0px 1px 5px 0px rgba(0, 0, 0, 0.1)',
            bgcolor: effectiveTheme === 'dark' ? '#000000' : COLORS.brand.orange.main,
            backgroundImage: effectiveTheme === 'dark'
              ? 'linear-gradient(to bottom, #000000, #202124)'
              : 'none',
            borderBottom: effectiveTheme === 'dark' ? 'none' : undefined,
            borderTop: 'none',
            color: '#ffffff'
          }}
        >
          <Toolbar sx={{ px: { xs: 2, sm: 3 } }}>
            {user && isMobile && (
              <IconButton
                edge="start"
                color="inherit"
                aria-label="menu"
                onClick={handleDrawerToggle}
                sx={{ mr: 2 }}
              >
                <MenuIcon />
              </IconButton>
            )}

            <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center' }}>
              <RouterLink to="/" style={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}>
                <img
                  src="/gobel-logo.svg"
                  alt="Gobel Power"
                  style={{
                    height: '30px',
                    filter: 'brightness(0) invert(1)'
                  }}
                />
              </RouterLink>
            </Box>

            {/* Theme Toggle */}
            <IconButton
              color="inherit"
              onClick={toggleTheme}
              sx={{ ml: 1 }}
              title={`Theme: ${themeMode}`}
            >
              {themeMode === 'light' ? <LightModeIcon /> : <DarkModeIcon />}
            </IconButton>

            {/* Language Menu */}
            <IconButton
              color="inherit"
              onClick={handleLanguageMenuOpen}
              sx={{ ml: 2 }}
            >
              <LanguageIcon />
            </IconButton>

            <Menu
              anchorEl={languageMenuAnchor}
              open={Boolean(languageMenuAnchor)}
              onClose={handleLanguageMenuClose}
            >
              <MenuItem onClick={() => handleLanguageChange('en')}>English</MenuItem>
              <MenuItem onClick={() => handleLanguageChange('de')}>Deutsch</MenuItem>
              <MenuItem onClick={() => handleLanguageChange('es')}>Español</MenuItem>
              <MenuItem onClick={() => handleLanguageChange('it')}>Italiano</MenuItem>
              <MenuItem onClick={() => handleLanguageChange('fr')}>Français</MenuItem>
              <MenuItem onClick={() => handleLanguageChange('pl')}>Polski</MenuItem>
              <MenuItem onClick={() => handleLanguageChange('zh')}>中文</MenuItem>
            </Menu>

            {/* User Menu */}
            {user ? (
              <>
                <Button
                  color="inherit"
                  onClick={handleProfileMenuOpen}
                  startIcon={
                    <Avatar
                      src={getUploadUrl(user.avatar)}
                      sx={{ width: 24, height: 24, bgcolor: COLORS.brand.orange.dark }}
                    >
                      {user.username.charAt(0).toUpperCase()}
                    </Avatar>
                  }
                  sx={{ ml: 2, ...(isMobile && { mr: -1.5, minWidth: 'auto', px: 1 }) }}
                >
                  {!isMobile && user.username}
                </Button>

                <Menu
                  anchorEl={profileMenuAnchor}
                  open={Boolean(profileMenuAnchor)}
                  onClose={handleProfileMenuClose}
                >
                  <MenuItem onClick={() => handleNavigate('/settings')}>
                    {t('common.settings')}
                  </MenuItem>
                  <MenuItem onClick={handleLogout}>
                    {t('app.logout')}
                  </MenuItem>
                </Menu>
              </>
            ) : (
              <>
                <Button
                  color="inherit"
                  onClick={() => navigate('/login')}
                >
                  {t('app.login')}
                </Button>
                <Button
                  color="inherit"
                  onClick={() => navigate('/register')}
                >
                  {t('app.register')}
                </Button>
              </>
            )}
          </Toolbar>
        </AppBar>
      </Slide>

      {/* Sidebar */}
      <Sidebar mobileOpen={mobileOpen} onDrawerToggle={handleDrawerToggle} />
    </Box>
  );
}

export { drawerWidth };
export default Navbar;